/**
 * Profile claim confirmation page.
 *
 * Public page that confirms a cross-org profile move using a token from the
 * confirmation email. No authentication required — the token is the auth.
 */

import { trpcClient } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  token: z.string().min(1, "Confirmation token is required"),
});

export const Route = createFileRoute("/profiles/claim-confirm")({
  validateSearch: searchSchema,
  component: ProfileClaimConfirmPage,
});

type ConfirmState =
  | { status: "confirming" }
  | { status: "success"; profileName: string; email: string }
  | { status: "error"; message: string };

function ProfileClaimConfirmPage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<ConfirmState>({ status: "confirming" });
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    if (hasConfirmedRef.current) return;
    hasConfirmedRef.current = true;

    const confirm = async () => {
      try {
        const result = await trpcClient.profile.confirmProfileClaim.mutate({
          token,
        });
        setState({
          status: "success",
          profileName: result.profileName,
          email: result.email ?? "",
        });
      } catch (err) {
        setState({
          status: "error",
          message: (err as Error)?.message || "Failed to confirm profile move",
        });
      }
    };

    confirm();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {state.status === "confirming" && (
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            )}
            {state.status === "success" && (
              <CheckCircle className="h-12 w-12 text-green-500" />
            )}
            {state.status === "error" && (
              <AlertCircle className="h-12 w-12 text-destructive" />
            )}
          </div>
          <CardTitle>
            {state.status === "confirming" && "Confirming Move..."}
            {state.status === "success" && "Profile Moved!"}
            {state.status === "error" && "Confirmation Failed"}
          </CardTitle>
          <CardDescription>
            {state.status === "confirming" &&
              "Please wait while we confirm your profile move."}
            {state.status === "success" && (
              <>
                The profile <strong>{state.profileName}</strong> has been
                successfully moved to the requesting account.
              </>
            )}
            {state.status === "error" && state.message}
          </CardDescription>
        </CardHeader>
        {(state.status === "success" || state.status === "error") && (
          <CardContent className="text-center">
            <Link to="/" className="text-primary hover:underline">
              Go to home
            </Link>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
